import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, CustomFieldDefinition, RiskCategory, KRI, KRIRiskLink } from '../lib/api'
import { useUnsavedChanges, isFormDirty } from '../hooks/useUnsavedChanges'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import CustomFieldsForm from '../components/CustomFieldsForm'
import {
  AlertTriangle,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  User,
  Edit2,
  Trash2,
  X,
  HelpCircle,
  Link2,
  Unlink,
  FileText,
  Grid3X3,
  List,
  Download,
  BookOpen,
  Info,
  Shield,
  Sparkles,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Zap,
  BarChart3,
  TrendingDown,
  Layers,
  Folder,
  Tag,
  Palette,
  Settings2,
  RefreshCw,
  Gauge,
  Target,
  Sliders,
  History,
  Save,
  Clock,
  Calendar,
  ClipboardCheck,
} from 'lucide-react'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Legend,
} from 'recharts'

interface Risk {
  id: string
  title: string
  description?: string
  category?: string // Legacy field (text)
  category_id?: string // New: FK to risk_categories table
  category_name?: string // Joined from risk_categories
  category_color?: string // Joined from risk_categories
  category_icon?: string // Joined from risk_categories
  likelihood: number
  impact: number
  risk_score: number
  residual_likelihood?: number
  residual_impact?: number
  residual_risk_score?: number
  control_effectiveness_rating?: number
  status: string
  owner_id?: string
  owner_username?: string
  mitigation_plan?: string
  due_date?: number
  business_unit?: string // Issue #153: Risk aggregation
  department?: string // Issue #153: Risk aggregation
  linked_controls?: number
  linked_evidence?: number
  created_at: number
  custom_fields?: Record<string, any>
}

interface UserOption {
  id: string
  username: string
}

interface LinkedEvidence {
  id: string
  title: string
  relationship_type: string
  notes: string | null
  linked_at: number
  linked_by: string | null
  evidence_type: string
  status: string
  description: string | null
  control_id: string | null
}

interface AvailableEvidence {
  id: string
  note?: string
  file_name?: string
  timestamp: number
  control_number?: string
  control_name?: string
  uploaded_by_username?: string
}

// Control effectiveness analysis (Issue #146)
interface ControlBreakdown {
  id: string
  control_number: string
  name: string
  status: string
  effectiveness_rating: number | null
  effectiveness_label: string
  risk_reduction_percentage: number
  has_gap: boolean
}

interface ControlEffectivenessAnalysis {
  average_effectiveness_rating: number | null
  effectiveness_percentage: number
  suggested_residual_likelihood: number | null
  suggested_residual_impact: number | null
  suggested_residual_score: number | null
  controls_with_rating: number
  controls_without_rating: number
  total_linked_controls: number
  control_breakdown: ControlBreakdown[]
  warnings: string[]
}

// Heat map data point
interface HeatMapDataPoint {
  likelihood: number
  impact: number
  count: number
  risks: { id: string; title: string }[]
}

// Risk aggregation data (Issue #153)
interface RiskAggregation {
  group_name: string
  group_id?: string
  risk_count: number
  total_inherent_score: number
  total_residual_score: number
  avg_inherent_score: number
  avg_residual_score: number
  max_inherent_score: number
  max_residual_score: number
  reduction_percentage: number
  critical_count: number
  high_count: number
  medium_count: number
  low_count: number
  open_count: number
  mitigating_count: number
  mitigated_count: number
  accepted_count: number
  closed_count: number
}

interface AggregationSummary {
  total_risks: number
  total_inherent_score: number
  total_residual_score: number
  total_exposure: number
  reduction_percentage: number
  avg_inherent_score: number
  avg_residual_score: number
  max_inherent_score: number
  max_residual_score: number
  active_count: number
  overdue_count: number
  severity_distribution: {
    critical: number
    high: number
    medium: number
    low: number
  }
}

const statusConfig: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'badge-red' },
  mitigating: { label: 'Mitigating', color: 'badge-yellow' },
  mitigated: { label: 'Mitigated', color: 'badge-green' },
  accepted: { label: 'Accepted', color: 'badge-blue' },
  closed: { label: 'Closed', color: 'badge-gray' },
}

const categoryOptions = [
  'technical',
  'operational',
  'financial',
  'compliance',
  'strategic',
]

const getRiskLevel = (score: number) => {
  if (score >= 20) return { label: 'Critical', color: 'bg-red-600' }
  if (score >= 12) return { label: 'High', color: 'bg-orange-500' }
  if (score >= 6) return { label: 'Medium', color: 'bg-yellow-500' }
  return { label: 'Low', color: 'bg-green-500' }
}

// Get color for heat map cell based on risk score
const getHeatMapColor = (likelihood: number, impact: number): string => {
  const score = likelihood * impact
  if (score >= 20) return '#dc2626' // red-600
  if (score >= 12) return '#f97316' // orange-500
  if (score >= 6) return '#eab308' // yellow-500
  return '#22c55e' // green-500
}

// Likelihood scale definitions
const likelihoodScale = [
  { value: 1, label: 'Rare', description: 'May occur only in exceptional circumstances. Less than 10% chance.' },
  { value: 2, label: 'Unlikely', description: 'Could occur at some time but is not expected. 10-25% chance.' },
  { value: 3, label: 'Possible', description: 'Might occur at some time. 25-50% chance.' },
  { value: 4, label: 'Likely', description: 'Will probably occur in most circumstances. 50-75% chance.' },
  { value: 5, label: 'Almost Certain', description: 'Expected to occur in most circumstances. Greater than 75% chance.' },
]

// Impact scale definitions
const impactScale = [
  { value: 1, label: 'Negligible', description: 'Minimal impact on operations, finances, or reputation. Easily absorbed.' },
  { value: 2, label: 'Minor', description: 'Limited impact requiring some management effort to address.' },
  { value: 3, label: 'Moderate', description: 'Noticeable impact on operations or finances. Requires significant effort to manage.' },
  { value: 4, label: 'Major', description: 'Significant impact on business objectives. May require executive intervention.' },
  { value: 5, label: 'Catastrophic', description: 'Severe impact threatening business viability or causing major regulatory/legal consequences.' },
]

// Tooltip for risk scoring methodology (quick reference)
const ScoringMethodologyTooltip = () => (
  <div className="relative inline-block group">
    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
      <p className="font-semibold mb-2">Risk Scoring Methodology</p>
      <p className="mb-2">
        <strong>Risk Score = Likelihood x Impact</strong>
      </p>
      <div className="space-y-1">
        <p><span className="inline-block w-16 px-1 bg-red-600 rounded text-center">20-25</span> Critical</p>
        <p><span className="inline-block w-16 px-1 bg-orange-500 rounded text-center">12-19</span> High</p>
        <p><span className="inline-block w-16 px-1 bg-yellow-500 rounded text-center">6-11</span> Medium</p>
        <p><span className="inline-block w-16 px-1 bg-green-500 rounded text-center">1-5</span> Low</p>
      </div>
      <p className="mt-2 text-gray-300">
        Likelihood: 1 (Rare) to 5 (Almost Certain)<br />
        Impact: 1 (Negligible) to 5 (Catastrophic)
      </p>
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-8 border-transparent border-t-gray-900"></div>
    </div>
  </div>
)

// Full Risk Scoring Methodology Modal
interface MethodologyModalProps {
  isOpen: boolean
  onClose: () => void
}

const RiskScoringMethodologyModal = ({ isOpen, onClose }: MethodologyModalProps) => {
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Risk Scoring Methodology</h2>
              <p className="text-sm text-gray-500">How risk scores are calculated and what they mean</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Overview Section */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Overview
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-3">
                Risk scoring provides a quantitative method to prioritize risks based on their potential impact and probability of occurrence.
                This methodology uses a 5x5 risk matrix, multiplying likelihood and impact ratings to produce a risk score between 1 and 25.
              </p>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <p className="text-center font-mono text-lg">
                  <span className="font-semibold text-gray-900">Risk Score</span> = <span className="text-blue-600">Likelihood</span> x <span className="text-orange-600">Impact</span>
                </p>
                <p className="text-center text-xs text-gray-500 mt-1">
                  Score Range: 1 (lowest) to 25 (highest)
                </p>
              </div>
            </div>
          </section>

          {/* Risk Levels Section */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Risk Levels</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-4 h-4 rounded bg-red-600"></span>
                  <span className="font-semibold text-red-700">Critical</span>
                </div>
                <p className="text-2xl font-bold text-red-600 mb-1">20-25</p>
                <p className="text-xs text-gray-600">Immediate action required. Executive attention and dedicated resources needed.</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-4 h-4 rounded bg-orange-500"></span>
                  <span className="font-semibold text-orange-700">High</span>
                </div>
                <p className="text-2xl font-bold text-orange-600 mb-1">12-19</p>
                <p className="text-xs text-gray-600">Prioritized treatment required. Senior management attention needed.</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-4 h-4 rounded bg-yellow-500"></span>
                  <span className="font-semibold text-yellow-700">Medium</span>
                </div>
                <p className="text-2xl font-bold text-yellow-600 mb-1">6-11</p>
                <p className="text-xs text-gray-600">Treatment required within defined timeframe. Management responsibility assigned.</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-4 h-4 rounded bg-green-500"></span>
                  <span className="font-semibold text-green-700">Low</span>
                </div>
                <p className="text-2xl font-bold text-green-600 mb-1">1-5</p>
                <p className="text-xs text-gray-600">Acceptable risk level. Monitor and review periodically.</p>
              </div>
            </div>
          </section>

          {/* Likelihood Scale Section */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Likelihood Scale (1-5)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Likelihood measures the probability of a risk event occurring within a defined time period (typically 12 months).
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {likelihoodScale.map((item) => (
                    <tr key={item.value} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
                          {item.value}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{item.label}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Impact Scale Section */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Impact Scale (1-5)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Impact measures the potential consequences if a risk event occurs, considering financial, operational, reputational, and regulatory factors.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {impactScale.map((item) => (
                    <tr key={item.value} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-semibold text-sm">
                          {item.value}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{item.label}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Risk Matrix Section */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Risk Matrix</h3>
            <p className="text-sm text-gray-600 mb-3">
              The 5x5 risk matrix shows all possible score combinations. Use this to quickly identify where a risk falls.
            </p>
            <div className="overflow-x-auto">
              <table className="border-collapse">
                <thead>
                  <tr>
                    <th className="p-2"></th>
                    <th className="p-2"></th>
                    <th colSpan={5} className="text-center text-sm font-semibold text-gray-700 pb-2">
                      Impact
                    </th>
                  </tr>
                  <tr>
                    <th className="p-2"></th>
                    <th className="p-2"></th>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <th key={i} className="p-2 text-center text-xs font-medium text-gray-500 w-14">
                        {i}<br />
                        <span className="text-[10px]">{impactScale[i - 1].label}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[5, 4, 3, 2, 1].map((likelihood) => (
                    <tr key={likelihood}>
                      {likelihood === 5 && (
                        <th rowSpan={5} className="p-2 text-sm font-semibold text-gray-700 writing-mode-vertical">
                          <span className="inline-block transform -rotate-180" style={{ writingMode: 'vertical-rl' }}>
                            Likelihood
                          </span>
                        </th>
                      )}
                      <td className="p-2 text-center text-xs font-medium text-gray-500">
                        {likelihood}<br />
                        <span className="text-[10px]">{likelihoodScale[likelihood - 1].label}</span>
                      </td>
                      {[1, 2, 3, 4, 5].map((impact) => {
                        const score = likelihood * impact
                        const level = getRiskLevel(score)
                        return (
                          <td
                            key={impact}
                            className={`p-2 text-center text-white font-semibold text-sm border border-white ${level.color}`}
                            style={{ width: '56px', height: '56px' }}
                          >
                            {score}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Residual Risk Section */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Inherent vs Residual Risk</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Inherent Risk</h4>
                <p className="text-sm text-gray-600">
                  The raw risk level before any controls or mitigations are applied. This represents the "worst case" scenario
                  if no protective measures were in place.
                </p>
                <p className="text-xs text-gray-500 mt-2 font-mono">
                  Inherent Risk = Likelihood x Impact
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Residual Risk</h4>
                <p className="text-sm text-gray-600">
                  The remaining risk level after controls and mitigations have been implemented. This shows the effectiveness
                  of your risk treatment efforts.
                </p>
                <p className="text-xs text-gray-500 mt-2 font-mono">
                  Residual Risk = Residual Likelihood x Residual Impact
                </p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">Risk Reduction</h4>
              <p className="text-sm text-gray-600">
                The goal of risk treatment is to reduce residual risk to an acceptable level. The percentage reduction
                is calculated as:
              </p>
              <p className="text-sm text-gray-700 mt-2 font-mono text-center">
                Reduction % = ((Inherent Risk - Residual Risk) / Inherent Risk) x 100
              </p>
            </div>
          </section>

          {/* Best Practices Section */}
          <section>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Best Practices</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">1.</span>
                <span><strong>Be consistent:</strong> Use the same criteria across all risk assessments to ensure comparability.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">2.</span>
                <span><strong>Document reasoning:</strong> Record why specific likelihood and impact ratings were chosen.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">3.</span>
                <span><strong>Review regularly:</strong> Risk scores should be reassessed periodically as conditions change.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">4.</span>
                <span><strong>Link controls:</strong> Associate mitigating controls with risks to track residual risk accurately.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">5.</span>
                <span><strong>Involve stakeholders:</strong> Risk assessments benefit from input across different perspectives.</span>
              </li>
            </ul>
          </section>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t flex justify-end">
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// Category Tree Item component for hierarchical display (Issue #100)
interface CategoryTreeItemProps {
  category: RiskCategory
  level: number
  expandedCategories: Set<string>
  onToggleExpand: (id: string) => void
  onEdit: (category: RiskCategory) => void
  onDelete: (category: RiskCategory) => void
  onAddChild: (parentId: string) => void
  onCategoryClick: (categoryId: string) => void
  riskCount: number
  flatCategories: RiskCategory[]
}

const CategoryTreeItem = ({
  category,
  level,
  expandedCategories,
  onToggleExpand,
  onEdit,
  onDelete,
  onAddChild,
  onCategoryClick,
  riskCount,
  flatCategories,
}: CategoryTreeItemProps) => {
  const hasChildren = category.children && category.children.length > 0
  const isExpanded = expandedCategories.has(category.id)

  // Count total risks for this category and all its descendants
  const getTotalRiskCount = (cat: RiskCategory): number => {
    let total = flatCategories.filter(c => c.id === cat.id).length > 0 ? riskCount : 0
    if (cat.children) {
      for (const child of cat.children) {
        total += getTotalRiskCount(child)
      }
    }
    return total
  }

  return (
    <div>
      <div
        className={`flex items-center justify-between p-4 hover:bg-gray-50 ${level > 0 ? 'border-l-2 border-gray-200' : ''}`}
        style={{ paddingLeft: `${16 + level * 24}px` }}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Expand/Collapse button for categories with children */}
          {hasChildren ? (
            <button
              onClick={() => onToggleExpand(category.id)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
            </button>
          ) : (
            <div className="w-6" /> // Spacer
          )}

          {/* Category color and icon */}
          <div
            className="flex items-center justify-center w-8 h-8 rounded-lg"
            style={{ backgroundColor: category.color }}
          >
            <Folder className="h-4 w-4 text-white" />
          </div>

          {/* Category name and description */}
          <div className="flex-1 min-w-0">
            <button
              onClick={() => onCategoryClick(category.id)}
              className="font-medium text-gray-900 hover:text-primary-600 text-left"
            >
              {category.name}
            </button>
            {category.description && (
              <p className="text-sm text-gray-500 truncate">{category.description}</p>
            )}
          </div>

          {/* Risk count badge */}
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
            {riskCount} risk{riskCount !== 1 ? 's' : ''}
          </span>

          {/* System category badge */}
          {category.is_system === 1 && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
              System
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-4">
          <button
            onClick={() => onAddChild(category.id)}
            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded"
            title="Add sub-category"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit(category)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title="Edit category"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          {category.is_system !== 1 && (
            <button
              onClick={() => onDelete(category)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
              title="Delete category"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Render children if expanded */}
      {hasChildren && isExpanded && (
        <div>
          {category.children!.map((child) => (
            <CategoryTreeItem
              key={child.id}
              category={child}
              level={level + 1}
              expandedCategories={expandedCategories}
              onToggleExpand={onToggleExpand}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              onCategoryClick={onCategoryClick}
              riskCount={flatCategories.filter(c => c.id === child.id).length > 0 ? 0 : 0} // Will be calculated properly
              flatCategories={flatCategories}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function Risks() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [risks, setRisks] = useState<Risk[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '')
  const [severityFilter, setSeverityFilter] = useState(searchParams.get('severity') || '')
  // Auto-expand filters if any filter is pre-set from URL params
  const [showFilters, setShowFilters] = useState(
    !!(searchParams.get('status') || searchParams.get('category') || searchParams.get('severity'))
  )
  const [showModal, setShowModal] = useState(false)
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '', // Legacy
    category_id: '', // New: FK to risk_categories
    likelihood: 3,
    impact: 3,
    residual_likelihood: 0,
    residual_impact: 0,
    control_effectiveness_rating: 0,
    status: 'open',
    owner_id: '',
    mitigation_plan: '',
    business_unit: '', // Issue #153: Risk aggregation
    department: '', // Issue #153: Risk aggregation
    custom_fields: {} as Record<string, any>,
  })
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showMethodologyModal, setShowMethodologyModal] = useState(false)
  const toast = useToast()
  const confirmDialog = useConfirm()

  // View mode: list, heat map, aggregated, or categories
  const [viewMode, setViewMode] = useState<'list' | 'heatmap' | 'aggregated' | 'categories' | 'insights'>('list')

  // Risk categories state (Issue #100)
  const [riskCategories, setRiskCategories] = useState<RiskCategory[]>([])
  const [flatCategories, setFlatCategories] = useState<RiskCategory[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [expandedCategoryFilter, setExpandedCategoryFilter] = useState<Set<string>>(new Set())

  // Category management state (Issue #100)
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<RiskCategory | null>(null)
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    parent_id: '',
    color: '#6B7280',
    icon: 'folder',
    sort_order: 0,
    appetite_id: '',  // FK to risk_appetite.id
  })
  const [savingCategory, setSavingCategory] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [seedingCategories, setSeedingCategories] = useState(false)

  // Aggregation state (Issue #153)
  const [aggregationGroupBy, setAggregationGroupBy] = useState<'category' | 'owner' | 'status' | 'treatment' | 'control_family' | 'business_unit' | 'department'>('category')
  const [aggregations, setAggregations] = useState<RiskAggregation[]>([])
  const [aggregationSummary, setAggregationSummary] = useState<AggregationSummary | null>(null)

  // Risk Insights state (Issue #272)
  const [insightsLoading, setInsightsLoading] = useState(false)
  const [riskPosture, setRiskPosture] = useState<{
    by_severity: Array<{ severity: string; count: number; open_count: number }>
    totals: { critical: number; high: number; medium: number; low: number; total: number }
    risk_score: number
  } | null>(null)
  const [appetiteComparison, setAppetiteComparison] = useState<Array<{
    category: string
    display_name: string
    appetite_level: number
    tolerance_threshold: number
    max_exposure: number | null
    tolerance_breaches: number
    color: string | null
  }>>([])
  const [complianceSummary, setComplianceSummary] = useState<{ total_categories: number; compliant_categories: number; compliance_percentage: number } | null>(null)
  const [kriSummary, setKriSummary] = useState<{
    summary: { total: number; green: number; yellow: number; red: number; no_data: number }
    kris: Array<{
      id: string
      name: string
      category_name: string | null
      category_color: string | null
      current_value: number | null
      current_status: 'green' | 'yellow' | 'red' | null
      linked_risks_count: number
    }>
  } | null>(null)
  const [loadingAggregation, setLoadingAggregation] = useState(false)
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)

  // Custom fields state
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinition[]>([])
  const [loadingCustomFields, setLoadingCustomFields] = useState(false)

  // Evidence linking state
  const [linkedEvidence, setLinkedEvidence] = useState<LinkedEvidence[]>([])
  const [availableEvidence, setAvailableEvidence] = useState<AvailableEvidence[]>([])
  const [loadingEvidence, setLoadingEvidence] = useState(false)
  const [showEvidencePicker, setShowEvidencePicker] = useState(false)
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<string[]>([])
  const [evidenceRelationshipType, setEvidenceRelationshipType] = useState<'supporting' | 'mitigation' | 'assessment'>('supporting')
  const [linkingEvidence, setLinkingEvidence] = useState(false)

  // Control effectiveness analysis state (Issue #146)
  const [controlEffectivenessAnalysis, setControlEffectivenessAnalysis] = useState<ControlEffectivenessAnalysis | null>(null)
  const [loadingControlEffectiveness, setLoadingControlEffectiveness] = useState(false)
  const [applyingSuggestedResidual, setApplyingSuggestedResidual] = useState(false)

  // KRI linking state (Issue #127)
  const [linkedKRIs, setLinkedKRIs] = useState<(KRI & { linked_at?: number })[]>([])
  const [availableKRIs, setAvailableKRIs] = useState<KRI[]>([])
  const [loadingKRIs, setLoadingKRIs] = useState(false)
  const [showKRIPicker, setShowKRIPicker] = useState(false)
  const [selectedKRIIds, setSelectedKRIIds] = useState<string[]>([])
  const [linkingKRIs, setLinkingKRIs] = useState(false)

  // Risk Review state (Issue #70)
  const [risksDueForReview, setRisksDueForReview] = useState<Array<{
    id: string
    title: string
    status: string
    risk_score: number
    residual_risk_score: number
    review_frequency: string
    last_review_date: number | null
    next_review_date: number
    owner_id: string | null
    owner_username: string | null
    review_status: 'overdue' | 'due_soon'
  }>>([])
  const [loadingReviewsDue, setLoadingReviewsDue] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewingRisk, setReviewingRisk] = useState<Risk | null>(null)
  const [reviewFormData, setReviewFormData] = useState({
    likelihood: 3,
    impact: 3,
    residual_likelihood: 0,
    residual_impact: 0,
    status: 'open',
    notes: '',
  })
  const [savingReview, setSavingReview] = useState(false)
  const [showReviewHistoryModal, setShowReviewHistoryModal] = useState(false)
  const [reviewHistory, setReviewHistory] = useState<Array<{
    id: string
    reviewed_at: number
    reviewed_by: string
    reviewed_by_username: string
    reviewed_by_email: string
    reviewed_by_name: string
    likelihood_before: number | null
    impact_before: number | null
    residual_likelihood_before: number | null
    residual_impact_before: number | null
    status_before: string | null
    likelihood_after: number | null
    impact_after: number | null
    residual_likelihood_after: number | null
    residual_impact_after: number | null
    status_after: string | null
    outcome: string
    notes: string | null
    changes: Record<string, { old: any; new: any }> | null
  }>>([])
  const [loadingReviewHistory, setLoadingReviewHistory] = useState(false)

  // Heat map data
  const [heatMapData, setHeatMapData] = useState<HeatMapDataPoint[]>([])

  // Track initial form values for dirty detection
  const initialFormRef = useRef<typeof formData | null>(null)

  // Calculate if form has unsaved changes (only when modal is open)
  const hasUnsavedChanges = useMemo(() => {
    if (!showModal || !initialFormRef.current) return false
    return isFormDirty(formData, initialFormRef.current)
  }, [showModal, formData])

  // Warn on navigation if there are unsaved changes in the modal
  useUnsavedChanges(hasUnsavedChanges)

  const limit = 20

  // Build heat map data from risks
  const buildHeatMapData = useCallback((risksList: Risk[]): HeatMapDataPoint[] => {
    const matrix: Map<string, HeatMapDataPoint> = new Map()

    risksList
      .filter((r) => r.status === 'open' || r.status === 'mitigating')
      .forEach((risk) => {
        const key = `${risk.likelihood}-${risk.impact}`
        const existing = matrix.get(key)
        if (existing) {
          existing.count++
          existing.risks.push({ id: risk.id, title: risk.title })
        } else {
          matrix.set(key, {
            likelihood: risk.likelihood,
            impact: risk.impact,
            count: 1,
            risks: [{ id: risk.id, title: risk.title }],
          })
        }
      })

    return Array.from(matrix.values())
  }, [])

  useEffect(() => {
    fetchUsers()
    fetchCustomFieldDefinitions()
    fetchRiskCategories()
    fetchRisksDueForReview()
  }, [])

  useEffect(() => {
    fetchRisks()
  }, [page, search, statusFilter, categoryFilter, severityFilter])

  // Update heat map data when risks change
  useEffect(() => {
    if (viewMode === 'heatmap') {
      setHeatMapData(buildHeatMapData(risks))
    }
  }, [risks, viewMode, buildHeatMapData])

  // Fetch aggregation data when in aggregated view mode (Issue #153)
  useEffect(() => {
    if (viewMode === 'aggregated') {
      fetchAggregation()
    }
  }, [viewMode, aggregationGroupBy, statusFilter])

  // Fetch insights data when in insights view mode (Issue #272)
  useEffect(() => {
    if (viewMode === 'insights') {
      fetchInsightsData()
    }
  }, [viewMode])

  const fetchInsightsData = async () => {
    setInsightsLoading(true)
    try {
      const [postureRes, appetiteRes, kriRes] = await Promise.all([
        api.getRiskPosture(),
        api.getAppetiteVsActual(),
        api.getKRISummary(),
      ])
      if (postureRes.success) {
        setRiskPosture(postureRes.posture)
      }
      if (appetiteRes.success) {
        setAppetiteComparison(appetiteRes.comparison)
        setComplianceSummary(appetiteRes.summary)
      }
      if (kriRes.success) {
        setKriSummary({
          summary: kriRes.summary,
          kris: kriRes.kris,
        })
      }
    } catch (error) {
      console.error('Error fetching insights data:', error)
      toast.error('Failed to load insights data')
    } finally {
      setInsightsLoading(false)
    }
  }

  // Close export menu when clicking outside
  useEffect(() => {
    if (!showExportMenu) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-export-menu]')) {
        setShowExportMenu(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showExportMenu])

  async function fetchUsers() {
    try {
      const res = await api.getUsers()
      if (res.success) {
        setUsers(res.users)
      }
    } catch (err) {
      console.error('Failed to load users:', err)
    }
  }

  async function fetchCustomFieldDefinitions() {
    setLoadingCustomFields(true)
    try {
      const res = await api.getCustomFieldDefinitions('risk')
      if (res.success) {
        setCustomFieldDefinitions(res.definitions)
      }
    } catch (err) {
      console.error('Failed to load custom field definitions:', err)
    } finally {
      setLoadingCustomFields(false)
    }
  }

  // Fetch risk categories (Issue #100)
  async function fetchRiskCategories() {
    setLoadingCategories(true)
    try {
      // Fetch hierarchical categories for the filter
      const hierarchicalRes = await api.getRiskCategories()
      if (hierarchicalRes.success) {
        setRiskCategories(hierarchicalRes.categories)
      }
      // Also fetch flat list for the form dropdown
      const flatRes = await api.getRiskCategories({ flat: true })
      if (flatRes.success) {
        setFlatCategories(flatRes.categories)
      }
    } catch (err) {
      console.error('Failed to load risk categories:', err)
    } finally {
      setLoadingCategories(false)
    }
  }

  async function fetchRisks() {
    setLoading(true)
    try {
      const res = await api.getRisks({
        limit,
        offset: page * limit,
        search: search || undefined,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        severity: severityFilter || undefined,
      })
      if (res.success) {
        setRisks(res.risks)
        setTotal(res.pagination.total)
      }
    } catch (err) {
      console.error('Failed to load risks:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch risk aggregation data (Issue #153)
  async function fetchAggregation() {
    setLoadingAggregation(true)
    try {
      const res = await api.getRiskAggregation({
        groupBy: aggregationGroupBy,
        status: statusFilter || undefined,
      })
      if (res.success) {
        setAggregations(res.aggregations)
        setAggregationSummary(res.summary)
      }
    } catch (err) {
      console.error('Failed to load risk aggregation:', err)
      setAggregations([])
      setAggregationSummary(null)
    } finally {
      setLoadingAggregation(false)
    }
  }

  // Fetch risks due for review (Issue #70)
  async function fetchRisksDueForReview() {
    setLoadingReviewsDue(true)
    try {
      const res = await api.getRisksDueForReview()
      if (res.success) {
        setRisksDueForReview(res.risks)
      }
    } catch (err: any) {
      // Migration may not be applied yet - fail silently
      if (!err?.message?.includes('Migration required')) {
        console.error('Failed to load risks due for review:', err)
      }
      setRisksDueForReview([])
    } finally {
      setLoadingReviewsDue(false)
    }
  }

  // Fetch review history for a specific risk (Issue #70)
  async function fetchReviewHistory(riskId: string) {
    setLoadingReviewHistory(true)
    try {
      const res = await api.getRiskReviews(riskId)
      if (res.success) {
        setReviewHistory(res.reviews)
      }
    } catch (err: any) {
      if (!err?.message?.includes('Migration required')) {
        console.error('Failed to load review history:', err)
      }
      setReviewHistory([])
    } finally {
      setLoadingReviewHistory(false)
    }
  }

  // Record a risk review (Issue #70)
  async function handleRecordReview() {
    if (!reviewingRisk) return
    setSavingReview(true)
    try {
      const res = await api.recordRiskReview(reviewingRisk.id, {
        likelihood: reviewFormData.likelihood,
        impact: reviewFormData.impact,
        residual_likelihood: reviewFormData.residual_likelihood || undefined,
        residual_impact: reviewFormData.residual_impact || undefined,
        status: reviewFormData.status,
        notes: reviewFormData.notes || undefined,
      })
      if (res.success) {
        toast.success(`Risk reviewed successfully. Outcome: ${res.review.outcome.replace('_', ' ')}`)
        setShowReviewModal(false)
        setReviewingRisk(null)
        fetchRisks()
        fetchRisksDueForReview()
      }
    } catch (err: any) {
      console.error('Failed to record review:', err)
      toast.error(err?.message || 'Failed to record risk review')
    } finally {
      setSavingReview(false)
    }
  }

  // Open review modal for a risk
  function openReviewModal(risk: Risk) {
    setReviewingRisk(risk)
    setReviewFormData({
      likelihood: risk.likelihood,
      impact: risk.impact,
      residual_likelihood: risk.residual_likelihood || 0,
      residual_impact: risk.residual_impact || 0,
      status: risk.status,
      notes: '',
    })
    setShowReviewModal(true)
  }

  // Open review history modal
  function openReviewHistoryModal(risk: Risk) {
    setReviewingRisk(risk)
    fetchReviewHistory(risk.id)
    setShowReviewHistoryModal(true)
  }

  // Drill-down from aggregated view to filtered list
  const handleDrillDown = (groupName: string, groupId?: string) => {
    // Set the appropriate filter based on the aggregation group by
    const params = new URLSearchParams(searchParams)
    if (aggregationGroupBy === 'category') {
      if (groupName === 'Uncategorized') {
        params.delete('category')
      } else {
        params.set('category', groupName.toLowerCase())
      }
      setCategoryFilter(groupName === 'Uncategorized' ? '' : groupName.toLowerCase())
    } else if (aggregationGroupBy === 'owner') {
      if (groupName === 'Unassigned' || !groupId) {
        params.set('owner_id', 'unassigned')
      } else {
        params.set('owner_id', groupId)
      }
      // Note: owner filter requires URL param, not state variable
    } else if (aggregationGroupBy === 'status') {
      params.set('status', groupName.toLowerCase())
      setStatusFilter(groupName.toLowerCase())
    } else if (aggregationGroupBy === 'treatment') {
      // Treatment groups map to status filters
      const statusMap: Record<string, string> = {
        'Mitigated': 'mitigated',
        'Accepted': 'accepted',
        'Closed': 'closed',
        'Overdue': 'open', // Will show open risks, user can further filter
        'Has Plan': 'mitigating',
        'No Plan': 'open',
      }
      if (statusMap[groupName]) {
        params.set('status', statusMap[groupName])
        setStatusFilter(statusMap[groupName])
      }
    }
    // Switch to list view with filter applied
    setViewMode('list')
    setSearchParams(params)
    setShowFilters(true)
  }

  // Fetch linked evidence for a risk
  async function fetchLinkedEvidence(riskId: string) {
    setLoadingEvidence(true)
    try {
      const res = await api.getRiskEvidence(riskId)
      if (res.success) {
        setLinkedEvidence(res.evidence || [])
      }
    } catch (err) {
      console.error('Failed to load linked evidence:', err)
      setLinkedEvidence([])
    } finally {
      setLoadingEvidence(false)
    }
  }

  // Fetch available evidence for linking
  async function fetchAvailableEvidence() {
    try {
      const res = await api.getEvidence({ limit: 100 })
      if (res.success) {
        setAvailableEvidence(res.evidence || [])
      }
    } catch (err) {
      console.error('Failed to load available evidence:', err)
    }
  }

  // Link selected evidence to risk
  async function handleLinkEvidence() {
    if (!editingRisk || selectedEvidenceIds.length === 0) return

    setLinkingEvidence(true)
    try {
      await api.linkRiskEvidence(editingRisk.id, {
        evidence_ids: selectedEvidenceIds,
        relationship_type: evidenceRelationshipType,
      })
      await fetchLinkedEvidence(editingRisk.id)
      setShowEvidencePicker(false)
      setSelectedEvidenceIds([])
    } catch (err) {
      console.error('Failed to link evidence:', err)
    } finally {
      setLinkingEvidence(false)
    }
  }

  // Unlink evidence from risk
  async function handleUnlinkEvidence(evidenceId: string) {
    if (!editingRisk) return

    try {
      await api.unlinkRiskEvidence(editingRisk.id, evidenceId)
      await fetchLinkedEvidence(editingRisk.id)
    } catch (err) {
      console.error('Failed to unlink evidence:', err)
    }
  }

  // Fetch linked KRIs for a risk (Issue #127)
  async function fetchLinkedKRIs(riskId: string) {
    setLoadingKRIs(true)
    try {
      const res = await api.getKRIsForRisk(riskId)
      if (res.success) {
        setLinkedKRIs(res.kris || [])
      }
    } catch (err) {
      console.error('Failed to load linked KRIs:', err)
      setLinkedKRIs([])
    } finally {
      setLoadingKRIs(false)
    }
  }

  // Fetch available KRIs for linking
  async function fetchAvailableKRIs() {
    try {
      const res = await api.getKRIs()
      if (res.success) {
        // Filter out already linked KRIs
        const linkedIds = new Set(linkedKRIs.map(k => k.id))
        setAvailableKRIs((res.kris || []).filter((k: KRI) => !linkedIds.has(k.id)))
      }
    } catch (err) {
      console.error('Failed to load available KRIs:', err)
    }
  }

  // Link selected KRIs to risk
  async function handleLinkKRIs() {
    if (!editingRisk || selectedKRIIds.length === 0) return

    setLinkingKRIs(true)
    try {
      for (const kriId of selectedKRIIds) {
        await api.linkRiskToKRI(kriId, editingRisk.id)
      }
      await fetchLinkedKRIs(editingRisk.id)
      setShowKRIPicker(false)
      setSelectedKRIIds([])
      toast.success('KRIs linked successfully')
    } catch (err) {
      console.error('Failed to link KRIs:', err)
      toast.error('Failed to link KRIs')
    } finally {
      setLinkingKRIs(false)
    }
  }

  // Unlink KRI from risk
  async function handleUnlinkKRI(kriId: string) {
    if (!editingRisk) return

    try {
      await api.unlinkRiskFromKRI(kriId, editingRisk.id)
      await fetchLinkedKRIs(editingRisk.id)
      toast.success('KRI unlinked')
    } catch (err) {
      console.error('Failed to unlink KRI:', err)
      toast.error('Failed to unlink KRI')
    }
  }

  // Fetch control effectiveness analysis for a risk (Issue #146)
  async function fetchControlEffectiveness(riskId: string) {
    setLoadingControlEffectiveness(true)
    try {
      const res = await api.getRiskControlEffectiveness(riskId)
      if (res.success && res.analysis) {
        setControlEffectivenessAnalysis(res.analysis)
      }
    } catch (err) {
      console.error('Failed to load control effectiveness:', err)
      setControlEffectivenessAnalysis(null)
    } finally {
      setLoadingControlEffectiveness(false)
    }
  }

  // Apply suggested residual values from control effectiveness analysis
  async function handleApplySuggestedResidual() {
    if (!editingRisk) return

    setApplyingSuggestedResidual(true)
    try {
      const res = await api.applyRiskSuggestedResidual(editingRisk.id)
      if (res.success) {
        toast.success('Suggested residual values applied successfully')
        // Update form data with the applied values
        const applied = res.applied
        if (applied) {
          setFormData((prev) => ({
            ...prev,
            residual_likelihood: applied.residual_likelihood,
            residual_impact: applied.residual_impact,
            control_effectiveness_rating: applied.control_effectiveness_rating || 0,
          }))
        }
        // Refresh the control effectiveness analysis
        await fetchControlEffectiveness(editingRisk.id)
        // Refresh the risks list
        fetchRisks()
      }
    } catch (err: any) {
      console.error('Failed to apply suggested residual:', err)
      toast.error(err?.message || 'Failed to apply suggested residual values')
    } finally {
      setApplyingSuggestedResidual(false)
    }
  }

  // Category management functions (Issue #100)
  function openNewCategoryModal(parentId?: string) {
    setEditingCategory(null)
    setCategoryFormData({
      name: '',
      description: '',
      parent_id: parentId || '',
      color: '#6B7280',
      icon: 'folder',
      sort_order: 0,
      appetite_id: '',
    })
    setShowCategoryModal(true)
  }

  function openEditCategoryModal(category: RiskCategory) {
    setEditingCategory(category)
    setCategoryFormData({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id || '',
      color: category.color,
      icon: category.icon,
      sort_order: category.sort_order,
      appetite_id: (category as any).appetite_id || '',
    })
    setShowCategoryModal(true)
  }

  async function handleSaveCategory() {
    if (!categoryFormData.name.trim()) {
      toast.error('Category name is required')
      return
    }

    setSavingCategory(true)
    try {
      if (editingCategory) {
        // Update existing category
        const res = await api.updateRiskCategory(editingCategory.id, {
          name: categoryFormData.name,
          description: categoryFormData.description || undefined,
          parent_id: categoryFormData.parent_id || null,
          color: categoryFormData.color,
          icon: categoryFormData.icon,
          sort_order: categoryFormData.sort_order,
          appetite_id: categoryFormData.appetite_id || null,
        })
        if (res.success) {
          toast.success('Category updated successfully')
          setShowCategoryModal(false)
          fetchRiskCategories()
        }
      } else {
        // Create new category
        const res = await api.createRiskCategory({
          name: categoryFormData.name,
          description: categoryFormData.description || undefined,
          parent_id: categoryFormData.parent_id || undefined,
          color: categoryFormData.color,
          icon: categoryFormData.icon,
          sort_order: categoryFormData.sort_order,
          appetite_id: categoryFormData.appetite_id || undefined,
        })
        if (res.success) {
          toast.success('Category created successfully')
          setShowCategoryModal(false)
          fetchRiskCategories()
        }
      }
    } catch (err: any) {
      console.error('Failed to save category:', err)
      toast.error(err?.message || 'Failed to save category')
    } finally {
      setSavingCategory(false)
    }
  }

  async function handleDeleteCategory(category: RiskCategory) {
    const confirmed = await confirmDialog({
      title: 'Delete Category',
      message: `Are you sure you want to delete "${category.name}"? Risks assigned to this category will become uncategorized.`,
      confirmText: 'Delete',
      type: 'danger',
    })
    if (!confirmed) return

    try {
      const res = await api.deleteRiskCategory(category.id)
      if (res.success) {
        toast.success('Category deleted successfully')
        fetchRiskCategories()
      }
    } catch (err: any) {
      console.error('Failed to delete category:', err)
      toast.error(err?.message || 'Failed to delete category')
    }
  }

  async function handleSeedCategories() {
    setSeedingCategories(true)
    try {
      const res = await api.seedRiskCategories()
      if (res.success) {
        toast.success(`Seeded ${res.count || 0} default categories`)
        fetchRiskCategories()
      }
    } catch (err: any) {
      console.error('Failed to seed categories:', err)
      toast.error(err?.message || 'Failed to seed categories')
    } finally {
      setSeedingCategories(false)
    }
  }

  function toggleCategoryExpand(categoryId: string) {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  // Count risks for a category
  function countRisksForCategory(categoryId: string): number {
    return risks.filter(r => r.category_id === categoryId).length
  }

  const openNewRiskModal = () => {
    setEditingRisk(null)
    setLinkedEvidence([])
    setControlEffectivenessAnalysis(null)
    setLinkedKRIs([])
    const newFormData = {
      title: '',
      description: '',
      category: '', // Legacy
      category_id: '', // New: FK to risk_categories
      likelihood: 3,
      impact: 3,
      residual_likelihood: 0,
      residual_impact: 0,
      control_effectiveness_rating: 0,
      status: 'open',
      owner_id: '',
      mitigation_plan: '',
      business_unit: '', // Issue #153: Risk aggregation
      department: '', // Issue #153: Risk aggregation
      custom_fields: {} as Record<string, any>,
    }
    setFormData(newFormData)
    initialFormRef.current = { ...newFormData }
    setShowModal(true)
  }

  const openEditModal = (risk: Risk) => {
    setEditingRisk(risk)
    const editFormData = {
      title: risk.title,
      description: risk.description || '',
      category: risk.category || '', // Legacy
      category_id: risk.category_id || '', // New
      likelihood: risk.likelihood,
      impact: risk.impact,
      residual_likelihood: risk.residual_likelihood || 0,
      residual_impact: risk.residual_impact || 0,
      control_effectiveness_rating: risk.control_effectiveness_rating || 0,
      status: risk.status,
      owner_id: risk.owner_id || '',
      mitigation_plan: risk.mitigation_plan || '',
      business_unit: risk.business_unit || '', // Issue #153: Risk aggregation
      department: risk.department || '', // Issue #153: Risk aggregation
      custom_fields: risk.custom_fields || {},
    }
    setFormData(editFormData)
    initialFormRef.current = { ...editFormData }
    setShowModal(true)
    // Fetch linked evidence for this risk
    fetchLinkedEvidence(risk.id)
    // Fetch control effectiveness analysis (Issue #146)
    fetchControlEffectiveness(risk.id)
    // Fetch linked KRIs for this risk (Issue #127)
    fetchLinkedKRIs(risk.id)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editingRisk) {
        await api.updateRisk(editingRisk.id, formData)
      } else {
        await api.createRisk(formData)
      }
      // Reset initial ref before closing to prevent navigation warning
      initialFormRef.current = null
      setShowModal(false)
      fetchRisks()
    } catch (err: any) {
      console.error('Failed to save risk:', err)
      toast.error(err?.message || 'Failed to save risk')
    } finally {
      setSaving(false)
    }
  }

  const handleCloseModal = async () => {
    if (hasUnsavedChanges) {
      const confirmed = await confirmDialog({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close?',
        type: 'warning',
        confirmText: 'Discard',
      })
      if (!confirmed) return
    }
    initialFormRef.current = null
    setShowModal(false)
    setShowEvidencePicker(false)
    setSelectedEvidenceIds([])
    setLinkedEvidence([])
    setControlEffectivenessAnalysis(null)
    // Reset KRI state (Issue #127)
    setShowKRIPicker(false)
    setSelectedKRIIds([])
    setLinkedKRIs([])
  }

  const handleDelete = async (risk: Risk) => {
    const confirmed = await confirmDialog({
      title: 'Delete Risk',
      message: `Are you sure you want to delete "${risk.title}"?`,
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return
    try {
      await api.deleteRisk(risk.id)
      toast.success('Risk deleted successfully')
      fetchRisks()
    } catch (err) {
      console.error('Failed to delete risk:', err)
      toast.error('Failed to delete risk')
    }
  }

  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(true)
    setShowExportMenu(false)
    try {
      const blob = await api.exportRiskRegister(format, {
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
      })

      // Create download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().slice(0, 10)
      link.download = `risk-register-${timestamp}.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success(`Risk register exported as ${format.toUpperCase()}`)
    } catch (err) {
      console.error('Failed to export risk register:', err)
      toast.error('Failed to export risk register')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Risk Register</h1>
            <p className="text-gray-500 flex items-center gap-2">
              Track and manage organizational risks
              <ScoringMethodologyTooltip />
              <button
                onClick={() => setShowMethodologyModal(true)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                title="View scoring methodology documentation"
              >
                <BookOpen className="h-3.5 w-3.5" />
                Methodology
              </button>
            </p>
          </div>
          {/* Reviews Due Indicator (Issue #70) */}
          {risksDueForReview.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
              <Clock className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                {risksDueForReview.filter(r => r.review_status === 'overdue').length > 0 ? (
                  <>{risksDueForReview.filter(r => r.review_status === 'overdue').length} overdue</>
                ) : (
                  <>{risksDueForReview.length} due soon</>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* View mode toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="h-4 w-4" />
              List
            </button>
            <button
              onClick={() => setViewMode('heatmap')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'heatmap'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="h-4 w-4" />
              Heat Map
            </button>
            <button
              onClick={() => setViewMode('aggregated')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'aggregated'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="h-4 w-4" />
              Roll-up
            </button>
            <button
              onClick={() => setViewMode('categories')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'categories'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Folder className="h-4 w-4" />
              Categories
            </button>
            <button
              onClick={() => setViewMode('insights')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'insights'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Sparkles className="h-4 w-4" />
              Insights
            </button>
          </div>
          {/* Export dropdown */}
          <div className="relative" data-export-menu>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting || loading}
              className="btn-secondary disabled:opacity-50"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Export as CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Export as JSON
                </button>
              </div>
            )}
          </div>
          <button onClick={openNewRiskModal} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Add Risk
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search risks..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(0)
                const params = new URLSearchParams(searchParams)
                if (e.target.value) params.set('search', e.target.value)
                else params.delete('search')
                setSearchParams(params)
              }}
              className="input pl-10"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            <ChevronDown
              className={`h-4 w-4 ml-2 transition-transform ${
                showFilters ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value)
                  setPage(0)
                  const params = new URLSearchParams(searchParams)
                  if (e.target.value) params.set('status', e.target.value)
                  else params.delete('status')
                  setSearchParams(params)
                }}
                className="input"
              >
                <option value="">All Statuses</option>
                {Object.entries(statusConfig).map(([value, { label }]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value)
                  setPage(0)
                  const params = new URLSearchParams(searchParams)
                  if (e.target.value) params.set('category', e.target.value)
                  else params.delete('category')
                  setSearchParams(params)
                }}
                className="input"
              >
                <option value="">All Categories</option>
                {/* Show hierarchical categories if available, fallback to legacy options */}
                {flatCategories.length > 0 ? (
                  flatCategories.map((cat) => {
                    const parentCat = cat.parent_id ? flatCategories.find(p => p.id === cat.parent_id) : null
                    const displayName = parentCat ? `${parentCat.name} > ${cat.name}` : cat.name
                    return (
                      <option key={cat.id} value={cat.id}>
                        {displayName}
                      </option>
                    )
                  })
                ) : (
                  categoryOptions.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label className="label">Severity</label>
              <select
                value={severityFilter}
                onChange={(e) => {
                  setSeverityFilter(e.target.value)
                  setPage(0)
                  const params = new URLSearchParams(searchParams)
                  if (e.target.value) params.set('severity', e.target.value)
                  else params.delete('severity')
                  setSearchParams(params)
                }}
                className="input"
              >
                <option value="">All Severities</option>
                <option value="critical">Critical (Impact 4-5)</option>
                <option value="high">High (Impact 3+)</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Heat Map View */}
      {viewMode === 'heatmap' && (
        <div className="card p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Risk Heat Map</h2>
            <p className="text-sm text-gray-500">
              Active risks (Open/Mitigating) plotted by likelihood and impact. Bubble size indicates number of risks at that position.
            </p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : heatMapData.length === 0 ? (
            <div className="text-center py-12">
              <Grid3X3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No active risks to display</p>
            </div>
          ) : (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                  <XAxis
                    type="number"
                    dataKey="likelihood"
                    name="Likelihood"
                    domain={[0.5, 5.5]}
                    ticks={[1, 2, 3, 4, 5]}
                    tickFormatter={(value) => {
                      const labels: Record<number, string> = {
                        1: 'Rare',
                        2: 'Unlikely',
                        3: 'Possible',
                        4: 'Likely',
                        5: 'Almost Certain',
                      }
                      return labels[value] || ''
                    }}
                    label={{ value: 'Likelihood', position: 'bottom', offset: 40 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="impact"
                    name="Impact"
                    domain={[0.5, 5.5]}
                    ticks={[1, 2, 3, 4, 5]}
                    tickFormatter={(value) => {
                      const labels: Record<number, string> = {
                        1: 'Negligible',
                        2: 'Minor',
                        3: 'Moderate',
                        4: 'Major',
                        5: 'Catastrophic',
                      }
                      return labels[value] || ''
                    }}
                    label={{ value: 'Impact', angle: -90, position: 'insideLeft', offset: -10 }}
                  />
                  <ZAxis type="number" dataKey="count" range={[100, 400]} />
                  <RechartsTooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload as HeatMapDataPoint
                        return (
                          <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
                            <p className="font-semibold text-gray-900">
                              {data.count} risk{data.count > 1 ? 's' : ''}
                            </p>
                            <p className="text-sm text-gray-500">
                              Score: {data.likelihood * data.impact} (L:{data.likelihood} x I:{data.impact})
                            </p>
                            <div className="mt-2 text-xs text-gray-600 max-h-24 overflow-y-auto">
                              {data.risks.slice(0, 5).map((r) => (
                                <p key={r.id} className="truncate max-w-48">- {r.title}</p>
                              ))}
                              {data.risks.length > 5 && (
                                <p className="text-gray-400">...and {data.risks.length - 5} more</p>
                              )}
                            </div>
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  {/* Grid lines for visual reference */}
                  <ReferenceLine x={2.5} stroke="#e5e7eb" />
                  <ReferenceLine x={3.5} stroke="#e5e7eb" />
                  <ReferenceLine y={2.5} stroke="#e5e7eb" />
                  <ReferenceLine y={3.5} stroke="#e5e7eb" />
                  <Scatter data={heatMapData}>
                    {heatMapData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={getHeatMapColor(entry.likelihood, entry.impact)}
                        fillOpacity={0.8}
                        stroke={getHeatMapColor(entry.likelihood, entry.impact)}
                        strokeWidth={2}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* Legend */}
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span className="text-gray-600">Low (1-5)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500"></div>
              <span className="text-gray-600">Medium (6-11)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-500"></div>
              <span className="text-gray-600">High (12-19)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-600"></div>
              <span className="text-gray-600">Critical (20-25)</span>
            </div>
          </div>
        </div>
      )}

      {/* Aggregated/Roll-up View (Issue #153) */}
      {viewMode === 'aggregated' && (
        <div className="space-y-6">
          {/* Group By Selector */}
          <div className="card p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Group By:</span>
                <div className="flex flex-wrap bg-gray-100 rounded-lg p-1 gap-0.5">
                  <button
                    onClick={() => setAggregationGroupBy('category')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      aggregationGroupBy === 'category'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Category
                  </button>
                  <button
                    onClick={() => setAggregationGroupBy('owner')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      aggregationGroupBy === 'owner'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Owner
                  </button>
                  <button
                    onClick={() => setAggregationGroupBy('status')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      aggregationGroupBy === 'status'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Status
                  </button>
                  <button
                    onClick={() => setAggregationGroupBy('treatment')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      aggregationGroupBy === 'treatment'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Treatment
                  </button>
                  <button
                    onClick={() => setAggregationGroupBy('control_family')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      aggregationGroupBy === 'control_family'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Control Family
                  </button>
                  <button
                    onClick={() => setAggregationGroupBy('business_unit')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      aggregationGroupBy === 'business_unit'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Business Unit
                  </button>
                  <button
                    onClick={() => setAggregationGroupBy('department')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      aggregationGroupBy === 'department'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Department
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Click on any group to drill down to individual risks
              </p>
            </div>
          </div>

          {loadingAggregation ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : (
            <>
              {/* Summary Cards - Row 1 */}
              {aggregationSummary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  <div className="card p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Layers className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Risks</p>
                        <p className="text-2xl font-bold text-gray-900">{aggregationSummary.total_risks}</p>
                      </div>
                    </div>
                  </div>
                  <div className="card p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Active Risks</p>
                        <p className="text-2xl font-bold text-gray-900">{aggregationSummary.active_count || 0}</p>
                        <p className="text-xs text-gray-400">Open + Mitigating</p>
                      </div>
                    </div>
                  </div>
                  <div className="card p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Overdue</p>
                        <p className="text-2xl font-bold text-red-600">{aggregationSummary.overdue_count || 0}</p>
                        <p className="text-xs text-gray-400">Past due date</p>
                      </div>
                    </div>
                  </div>
                  <div className="card p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Gauge className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Exposure</p>
                        <p className="text-2xl font-bold text-gray-900">{aggregationSummary.total_exposure || aggregationSummary.total_residual_score}</p>
                        <p className="text-xs text-gray-400">Max: {aggregationSummary.max_residual_score || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="card p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <BarChart3 className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Avg Score</p>
                        <p className="text-2xl font-bold text-gray-900">{aggregationSummary.avg_residual_score}</p>
                        <p className="text-xs text-gray-400">Inherent: {aggregationSummary.avg_inherent_score}</p>
                      </div>
                    </div>
                  </div>
                  <div className="card p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-teal-100 rounded-lg">
                        <TrendingDown className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Risk Reduction</p>
                        <p className="text-2xl font-bold text-teal-600">{aggregationSummary.reduction_percentage}%</p>
                        <p className="text-xs text-gray-400">
                          -{aggregationSummary.total_inherent_score - aggregationSummary.total_residual_score} pts
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Severity Distribution Chart */}
              {aggregationSummary && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="card p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Severity Distribution</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Critical', value: aggregationSummary.severity_distribution.critical, fill: '#dc2626' },
                              { name: 'High', value: aggregationSummary.severity_distribution.high, fill: '#f97316' },
                              { name: 'Medium', value: aggregationSummary.severity_distribution.medium, fill: '#eab308' },
                              { name: 'Low', value: aggregationSummary.severity_distribution.low, fill: '#22c55e' },
                            ].filter(d => d.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          />
                          <RechartsTooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="card p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Risks by {
                      aggregationGroupBy === 'category' ? 'Category' :
                      aggregationGroupBy === 'owner' ? 'Owner' :
                      aggregationGroupBy === 'status' ? 'Status' :
                      aggregationGroupBy === 'treatment' ? 'Treatment Status' :
                      aggregationGroupBy === 'business_unit' ? 'Business Unit' :
                      aggregationGroupBy === 'department' ? 'Department' :
                      'Control Family'
                    }</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={aggregations.slice(0, 8)} layout="vertical">
                          <XAxis type="number" />
                          <YAxis
                            type="category"
                            dataKey="group_name"
                            width={100}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                          />
                          <RechartsTooltip
                            formatter={(value: number, name: string) => [value, name === 'risk_count' ? 'Risks' : name]}
                          />
                          <Bar dataKey="risk_count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {/* Aggregation Table */}
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {aggregationGroupBy === 'category' ? 'Category' :
                           aggregationGroupBy === 'owner' ? 'Owner' :
                           aggregationGroupBy === 'status' ? 'Status' :
                           aggregationGroupBy === 'treatment' ? 'Treatment Status' :
                           aggregationGroupBy === 'business_unit' ? 'Business Unit' :
                           aggregationGroupBy === 'department' ? 'Department' :
                           'Control Family'}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Risks
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Severity Breakdown
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Avg Inherent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Avg Residual
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reduction
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {aggregations.map((agg) => (
                        <tr key={agg.group_name} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {agg.group_name}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-900">{agg.risk_count}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              {agg.critical_count > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  {agg.critical_count} C
                                </span>
                              )}
                              {agg.high_count > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                  {agg.high_count} H
                                </span>
                              )}
                              {agg.medium_count > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {agg.medium_count} M
                                </span>
                              )}
                              {agg.low_count > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  {agg.low_count} L
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${getRiskLevel(agg.avg_inherent_score).color}`}>
                              {agg.avg_inherent_score}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${getRiskLevel(agg.avg_residual_score).color}`}>
                              {agg.avg_residual_score}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-teal-500 h-2 rounded-full"
                                  style={{ width: `${Math.min(100, agg.reduction_percentage)}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-600">{agg.reduction_percentage}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleDrillDown(agg.group_name, agg.group_id)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 ml-auto"
                            >
                              View Risks
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {aggregations.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                            No risk data available for aggregation
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Category Heat Maps */}
              {aggregationGroupBy === 'category' && aggregations.length > 0 && (
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Heat Maps by Category</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    5x5 risk matrix showing distribution of risks by category. Click a category to expand.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {aggregations.slice(0, 6).map((agg) => (
                      <div
                        key={agg.group_name}
                        className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => handleDrillDown(agg.group_name, agg.group_id)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900 capitalize">{agg.group_name}</h4>
                          <span className="text-sm text-gray-500">{agg.risk_count} risks</span>
                        </div>
                        {/* Mini heat map indicator */}
                        <div className="flex items-center gap-2 mb-2">
                          {agg.critical_count > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-red-600"></div>
                              <span className="text-xs text-gray-600">{agg.critical_count}</span>
                            </div>
                          )}
                          {agg.high_count > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-orange-500"></div>
                              <span className="text-xs text-gray-600">{agg.high_count}</span>
                            </div>
                          )}
                          {agg.medium_count > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-yellow-500"></div>
                              <span className="text-xs text-gray-600">{agg.medium_count}</span>
                            </div>
                          )}
                          {agg.low_count > 0 && (
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-green-500"></div>
                              <span className="text-xs text-gray-600">{agg.low_count}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Reduction: <span className="font-medium text-teal-600">{agg.reduction_percentage}%</span></span>
                          <ChevronRight className="h-4 w-4" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Categories Management View (Issue #100) */}
      {viewMode === 'categories' && (
        <div className="space-y-6">
          {/* Categories Header */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Risk Categories</h2>
                <p className="text-sm text-gray-500">
                  Organize risks into a hierarchical taxonomy for better classification and reporting.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {flatCategories.length === 0 && (
                  <button
                    onClick={handleSeedCategories}
                    disabled={seedingCategories}
                    className="btn-secondary"
                  >
                    {seedingCategories ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full mr-2" />
                        Seeding...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Seed Default Categories
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => openNewCategoryModal()}
                  className="btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </button>
              </div>
            </div>

            {/* Category Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{flatCategories.length}</p>
                <p className="text-sm text-gray-500">Total Categories</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{riskCategories.length}</p>
                <p className="text-sm text-gray-500">Top-Level</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">
                  {flatCategories.filter(c => c.parent_id).length}
                </p>
                <p className="text-sm text-gray-500">Sub-Categories</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {risks.filter(r => r.category_id).length}
                </p>
                <p className="text-sm text-gray-500">Categorized Risks</p>
              </div>
            </div>
          </div>

          {/* Categories List */}
          <div className="card overflow-hidden">
            {loadingCategories ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : riskCategories.length === 0 ? (
              <div className="text-center py-12">
                <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No categories defined</p>
                <p className="text-sm text-gray-400 mt-1">
                  Create custom categories or seed the default taxonomy to get started.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {riskCategories.map((category) => (
                  <CategoryTreeItem
                    key={category.id}
                    category={category}
                    level={0}
                    expandedCategories={expandedCategories}
                    onToggleExpand={toggleCategoryExpand}
                    onEdit={openEditCategoryModal}
                    onDelete={handleDeleteCategory}
                    onAddChild={openNewCategoryModal}
                    onCategoryClick={(catId) => {
                      setCategoryFilter(catId)
                      setViewMode('list')
                    }}
                    riskCount={countRisksForCategory(category.id)}
                    flatCategories={flatCategories}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Risk Insights Dashboard (Issue #272) */}
      {viewMode === 'insights' && (
        <div className="space-y-6">
          {insightsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : (
            <>
              {/* Summary Cards Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Risk Score Card */}
                <div className="card p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Risk Score</p>
                      <p className="text-3xl font-bold text-gray-900">{riskPosture?.risk_score ?? 0}%</p>
                    </div>
                    <div className={`p-3 rounded-full ${
                      (riskPosture?.risk_score ?? 0) >= 75 ? 'bg-red-100' :
                      (riskPosture?.risk_score ?? 0) >= 50 ? 'bg-yellow-100' :
                      (riskPosture?.risk_score ?? 0) >= 25 ? 'bg-orange-100' : 'bg-green-100'
                    }`}>
                      <Gauge className={`h-6 w-6 ${
                        (riskPosture?.risk_score ?? 0) >= 75 ? 'text-red-600' :
                        (riskPosture?.risk_score ?? 0) >= 50 ? 'text-yellow-600' :
                        (riskPosture?.risk_score ?? 0) >= 25 ? 'text-orange-600' : 'text-green-600'
                      }`} />
                    </div>
                  </div>
                </div>

                {/* Total Risks Card */}
                <div className="card p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Risks</p>
                      <p className="text-3xl font-bold text-gray-900">{riskPosture?.totals.total ?? 0}</p>
                    </div>
                    <div className="p-3 rounded-full bg-blue-100">
                      <AlertTriangle className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                {/* Appetite Compliance Card */}
                <div className="card p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Appetite Compliance</p>
                      <p className="text-3xl font-bold text-gray-900">{complianceSummary?.compliance_percentage ?? 100}%</p>
                    </div>
                    <div className={`p-3 rounded-full ${
                      (complianceSummary?.compliance_percentage ?? 100) >= 80 ? 'bg-green-100' :
                      (complianceSummary?.compliance_percentage ?? 100) >= 50 ? 'bg-yellow-100' : 'bg-red-100'
                    }`}>
                      <Target className={`h-6 w-6 ${
                        (complianceSummary?.compliance_percentage ?? 100) >= 80 ? 'text-green-600' :
                        (complianceSummary?.compliance_percentage ?? 100) >= 50 ? 'text-yellow-600' : 'text-red-600'
                      }`} />
                    </div>
                  </div>
                </div>

                {/* KRI Health Card */}
                <div className="card p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">KRI Health</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-full bg-green-500"></span>
                          <span className="text-lg font-bold">{kriSummary?.summary.green ?? 0}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                          <span className="text-lg font-bold">{kriSummary?.summary.yellow ?? 0}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-full bg-red-500"></span>
                          <span className="text-lg font-bold">{kriSummary?.summary.red ?? 0}</span>
                        </span>
                      </div>
                    </div>
                    <a href="/kris" className="p-3 rounded-full bg-purple-100 hover:bg-purple-200 transition-colors">
                      <Gauge className="h-6 w-6 text-purple-600" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Risk Posture by Severity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Posture by Severity</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Critical', count: riskPosture?.totals.critical ?? 0, color: 'bg-red-500' },
                      { label: 'High', count: riskPosture?.totals.high ?? 0, color: 'bg-orange-500' },
                      { label: 'Medium', count: riskPosture?.totals.medium ?? 0, color: 'bg-yellow-500' },
                      { label: 'Low', count: riskPosture?.totals.low ?? 0, color: 'bg-green-500' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-full ${item.color}`}></span>
                        <span className="text-sm text-gray-600 w-16">{item.label}</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-4">
                          <div
                            className={`${item.color} h-4 rounded-full transition-all duration-500`}
                            style={{ width: `${riskPosture?.totals.total ? (item.count / riskPosture.totals.total) * 100 : 0}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-8 text-right">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Appetite vs Actual */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Appetite vs Actual Exposure</h3>
                  {appetiteComparison.length === 0 ? (
                    <p className="text-gray-500 text-sm">No appetite categories configured</p>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {appetiteComparison.map((item) => {
                        const exposure = item.max_exposure ?? 0
                        const threshold = item.tolerance_threshold
                        const status = exposure > threshold ? 'breach' : 'ok'
                        return (
                          <div key={item.category} className="flex items-center gap-3">
                            <span
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: item.color || '#6B7280' }}
                            ></span>
                            <span className="text-sm text-gray-600 flex-1 truncate">{item.display_name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Threshold: {threshold}</span>
                              <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                                status === 'breach' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {exposure === 0 ? '-' : exposure}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* KRI Summary with Traffic Lights */}
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Key Risk Indicators (KRIs)</h3>
                  <a href="/kris" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
                    View All <ChevronRight className="h-4 w-4" />
                  </a>
                </div>
                {!kriSummary || kriSummary.kris.length === 0 ? (
                  <div className="text-center py-8">
                    <Gauge className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No KRIs configured</p>
                    <a href="/kris" className="btn-primary mt-3 inline-flex items-center gap-2">
                      <Plus className="h-4 w-4" /> Create KRI
                    </a>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {kriSummary.kris.slice(0, 6).map((kri) => (
                      <div
                        key={kri.id}
                        className="border rounded-lg p-4 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{kri.name}</p>
                            {kri.category_name && (
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <span
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: kri.category_color || '#6B7280' }}
                                ></span>
                                {kri.category_name}
                              </p>
                            )}
                          </div>
                          <div className={`w-4 h-4 rounded-full ${
                            kri.current_status === 'green' ? 'bg-green-500' :
                            kri.current_status === 'yellow' ? 'bg-yellow-500' :
                            kri.current_status === 'red' ? 'bg-red-500' : 'bg-gray-300'
                          }`}></div>
                        </div>
                        <div className="mt-3 flex items-end justify-between">
                          <div>
                            <p className="text-2xl font-bold text-gray-900">
                              {kri.current_value ?? '-'}
                            </p>
                          </div>
                          {kri.linked_risks_count > 0 && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Link2 className="h-3 w-3" />
                              {kri.linked_risks_count} risks
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Risks List */}
      {viewMode === 'list' && (
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : risks.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No risks found</p>
            <button onClick={openNewRiskModal} className="btn-primary mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add your first risk
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Risk
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Risk Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {risks.map((risk) => {
                    const status = statusConfig[risk.status] || statusConfig.open
                    const riskLevel = getRiskLevel(risk.risk_score)
                    return (
                      <tr key={risk.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <span className="text-sm font-medium text-gray-900">
                              {risk.title}
                            </span>
                            {risk.description && (
                              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                                {risk.description}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* Show category badge if category_id exists, fallback to legacy */}
                          {risk.category_name ? (
                            <span
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ backgroundColor: risk.category_color || '#6B7280' }}
                              title={risk.category_name}
                            >
                              <Tag className="h-3 w-3" />
                              {risk.category_name}
                            </span>
                          ) : risk.category ? (
                            <span className="text-sm text-gray-700 capitalize">
                              {risk.category}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${riskLevel.color}`}
                            >
                              {risk.risk_score}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({risk.likelihood}x{risk.impact})
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={status.color}>{status.label}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-700">
                            <User className="h-4 w-4 mr-1 text-gray-400" />
                            {risk.owner_username || 'Unassigned'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openReviewModal(risk)}
                              className="text-gray-400 hover:text-blue-600"
                              title="Record review"
                            >
                              <ClipboardCheck className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openReviewHistoryModal(risk)}
                              className="text-gray-400 hover:text-purple-600"
                              title="View review history"
                            >
                              <History className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openEditModal(risk)}
                              className="text-gray-400 hover:text-gray-600"
                              title="Edit risk"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(risk)}
                              className="text-gray-400 hover:text-red-600"
                              title="Delete risk"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of{' '}
                {total} risks
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {/* Risk Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingRisk ? 'Edit Risk' : 'Add Risk'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="label">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="input"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label flex items-center gap-2">
                    Category
                    {formData.category_id && (() => {
                      const selectedCat = flatCategories.find(c => c.id === formData.category_id)
                      return selectedCat && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: selectedCat.color }}
                        >
                          {selectedCat.name}
                        </span>
                      )
                    })()}
                  </label>
                  <select
                    value={flatCategories.length > 0 ? formData.category_id : formData.category}
                    onChange={(e) => {
                      if (flatCategories.length > 0) {
                        // Using new category system - set category_id (FK)
                        setFormData({ ...formData, category_id: e.target.value, category: '' })
                      } else {
                        // Using legacy category system - set category (text field)
                        setFormData({ ...formData, category: e.target.value, category_id: '' })
                      }
                    }}
                    className="input"
                  >
                    <option value="">Select category</option>
                    {/* Show hierarchical categories if available, fallback to legacy options */}
                    {flatCategories.length > 0 ? (
                      flatCategories.map((cat) => {
                        const parentCat = cat.parent_id ? flatCategories.find(p => p.id === cat.parent_id) : null
                        const displayName = parentCat ? `${parentCat.name} > ${cat.name}` : cat.name
                        return (
                          <option key={cat.id} value={cat.id}>
                            {displayName}
                          </option>
                        )
                      })
                    ) : (
                      categoryOptions.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div>
                  <label className="label">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    className="input"
                  >
                    {Object.entries(statusConfig).map(([value, { label }]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label flex items-center gap-1">
                    Likelihood (1-5) *
                    <div className="relative inline-block group">
                      <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                        <p className="font-semibold mb-1">Probability of occurrence:</p>
                        <ul className="space-y-0.5">
                          {likelihoodScale.map((item) => (
                            <li key={item.value}><strong>{item.value}:</strong> {item.label}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </label>
                  <select
                    value={formData.likelihood}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        likelihood: parseInt(e.target.value),
                      })
                    }
                    className="input"
                  >
                    {likelihoodScale.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.value} - {item.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {likelihoodScale[formData.likelihood - 1]?.description}
                  </p>
                </div>
                <div>
                  <label className="label flex items-center gap-1">
                    Impact (1-5) *
                    <div className="relative inline-block group">
                      <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                        <p className="font-semibold mb-1">Consequence if it occurs:</p>
                        <ul className="space-y-0.5">
                          {impactScale.map((item) => (
                            <li key={item.value}><strong>{item.value}:</strong> {item.label}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </label>
                  <select
                    value={formData.impact}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        impact: parseInt(e.target.value),
                      })
                    }
                    className="input"
                  >
                    {impactScale.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.value} - {item.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {impactScale[formData.impact - 1]?.description}
                  </p>
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    Inherent Risk Score
                    <ScoringMethodologyTooltip />
                  </span>
                  <span
                    className={`px-2 py-1 rounded text-white text-sm font-medium ${
                      getRiskLevel(formData.likelihood * formData.impact).color
                    }`}
                  >
                    {formData.likelihood * formData.impact} -{' '}
                    {getRiskLevel(formData.likelihood * formData.impact).label}
                  </span>
                </div>
              </div>

              {/* Residual Risk Tracking - Shown for both create and edit */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  Residual Risk (After Controls)
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  {editingRisk
                    ? 'Adjust the residual likelihood and impact based on the effectiveness of implemented controls and mitigations.'
                    : 'Optionally set expected residual risk values. You can update these later as controls are implemented.'}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label flex items-center gap-1">
                      Residual Likelihood (1-5)
                      <div className="relative inline-block group">
                        <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                          <p className="font-semibold mb-1">Probability after controls:</p>
                          <p className="text-gray-300">Select the likelihood of this risk occurring after mitigating controls have been implemented.</p>
                        </div>
                      </div>
                    </label>
                    <select
                      value={formData.residual_likelihood || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          residual_likelihood: parseInt(e.target.value),
                        })
                      }
                      className="input"
                    >
                      <option value={0}>Not Set</option>
                      {likelihoodScale.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.value} - {item.label}
                        </option>
                      ))}
                    </select>
                    {formData.residual_likelihood > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {likelihoodScale[formData.residual_likelihood - 1]?.description}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label flex items-center gap-1">
                      Residual Impact (1-5)
                      <div className="relative inline-block group">
                        <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                          <p className="font-semibold mb-1">Consequence after controls:</p>
                          <p className="text-gray-300">Select the potential impact if this risk occurs despite mitigating controls.</p>
                        </div>
                      </div>
                    </label>
                    <select
                      value={formData.residual_impact || 0}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          residual_impact: parseInt(e.target.value),
                        })
                      }
                      className="input"
                    >
                      <option value={0}>Not Set</option>
                      {impactScale.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.value} - {item.label}
                        </option>
                      ))}
                    </select>
                    {formData.residual_impact > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {impactScale[formData.residual_impact - 1]?.description}
                      </p>
                    )}
                  </div>
                </div>
                {/* Control Effectiveness Rating */}
                <div className="mt-4">
                  <label className="label flex items-center gap-1">
                    Control Effectiveness Rating
                    <div className="relative inline-block group">
                      <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                        <p className="font-semibold mb-1">Rate control effectiveness:</p>
                        <ul className="space-y-0.5">
                          <li><strong>1:</strong> Not Effective</li>
                          <li><strong>2:</strong> Minimally Effective</li>
                          <li><strong>3:</strong> Moderately Effective</li>
                          <li><strong>4:</strong> Effective</li>
                          <li><strong>5:</strong> Highly Effective</li>
                        </ul>
                      </div>
                    </div>
                  </label>
                  <select
                    value={formData.control_effectiveness_rating || 0}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        control_effectiveness_rating: parseInt(e.target.value),
                      })
                    }
                    className="input"
                  >
                    <option value={0}>Not Rated</option>
                    <option value={1}>1 - Not Effective</option>
                    <option value={2}>2 - Minimally Effective</option>
                    <option value={3}>3 - Moderately Effective</option>
                    <option value={4}>4 - Effective</option>
                    <option value={5}>5 - Highly Effective</option>
                  </select>
                </div>

                {/* Inherent vs Residual Risk Comparison */}
                {formData.residual_likelihood > 0 && formData.residual_impact > 0 && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Risk Reduction Summary</h4>

                    {/* Visual Comparison */}
                    <div className="flex items-center gap-4 mb-4">
                      {/* Inherent Score */}
                      <div className="flex-1 text-center">
                        <p className="text-xs text-gray-500 mb-1">Inherent</p>
                        <span
                          className={`inline-block px-3 py-1.5 rounded text-white text-lg font-bold ${
                            getRiskLevel(formData.likelihood * formData.impact).color
                          }`}
                        >
                          {formData.likelihood * formData.impact}
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          {getRiskLevel(formData.likelihood * formData.impact).label}
                        </p>
                      </div>

                      {/* Arrow */}
                      <div className="flex flex-col items-center">
                        <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                        {formData.likelihood * formData.impact > formData.residual_likelihood * formData.residual_impact && (
                          <span className="text-xs font-medium text-green-600">
                            -{formData.likelihood * formData.impact - formData.residual_likelihood * formData.residual_impact}
                          </span>
                        )}
                      </div>

                      {/* Residual Score */}
                      <div className="flex-1 text-center">
                        <p className="text-xs text-gray-500 mb-1">Residual</p>
                        <span
                          className={`inline-block px-3 py-1.5 rounded text-white text-lg font-bold ${
                            getRiskLevel(formData.residual_likelihood * formData.residual_impact).color
                          }`}
                        >
                          {formData.residual_likelihood * formData.residual_impact}
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          {getRiskLevel(formData.residual_likelihood * formData.residual_impact).label}
                        </p>
                      </div>
                    </div>

                    {/* Reduction Gauge */}
                    {(() => {
                      const inherent = formData.likelihood * formData.impact
                      const residual = formData.residual_likelihood * formData.residual_impact
                      const reduction = inherent > 0 ? Math.round((1 - residual / inherent) * 100) : 0
                      return inherent > residual ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">Risk Reduction</span>
                            <span className={`font-semibold ${
                              reduction > 50 ? 'text-green-600' : reduction >= 25 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {reduction}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className={`h-2.5 rounded-full ${
                                reduction > 50 ? 'bg-green-500' : reduction >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(100, reduction)}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-400">
                            <span className="text-red-400">&lt;25% Low</span>
                            <span className="text-yellow-400">25-50%</span>
                            <span className="text-green-400">&gt;50% High</span>
                          </div>
                        </div>
                      ) : null
                    })()}

                    {/* Effectiveness Rating Display */}
                    {formData.control_effectiveness_rating > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">Control Effectiveness</span>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= formData.control_effectiveness_rating ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                            <span className="ml-1 text-xs font-medium text-gray-600">
                              ({formData.control_effectiveness_rating}/5)
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Control Effectiveness Analysis Section (Issue #146) */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-purple-500" />
                    Linked Controls Effectiveness
                  </h3>
                </div>

                {!editingRisk ? (
                  <div className="space-y-4">
                    {/* Placeholder stats grid matching edit mode structure */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-gray-400">-</p>
                        <p className="text-xs text-gray-500">Linked Controls</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-gray-400">-</p>
                        <p className="text-xs text-gray-500">Avg Effectiveness</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-gray-400">-</p>
                        <p className="text-xs text-gray-500">Risk Reduction</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 italic text-center">
                      Save the risk first to link controls and see effectiveness analysis.
                    </p>
                  </div>
                ) : loadingControlEffectiveness ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                      </div>
                    ) : controlEffectivenessAnalysis ? (
                      <div className="space-y-4">
                        {/* Warnings */}
                        {controlEffectivenessAnalysis.warnings.length > 0 && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                              <div className="text-xs text-amber-800">
                                <p className="font-semibold mb-1">Control Gaps Detected</p>
                                <ul className="space-y-0.5 list-disc list-inside">
                                  {controlEffectivenessAnalysis.warnings.slice(0, 3).map((warning, idx) => (
                                    <li key={idx}>{warning}</li>
                                  ))}
                                  {controlEffectivenessAnalysis.warnings.length > 3 && (
                                    <li>...and {controlEffectivenessAnalysis.warnings.length - 3} more</li>
                                  )}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Summary Stats */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-gray-900">
                              {controlEffectivenessAnalysis.total_linked_controls}
                            </p>
                            <p className="text-xs text-gray-500">Linked Controls</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-purple-600">
                              {controlEffectivenessAnalysis.average_effectiveness_rating?.toFixed(1) || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">Avg Effectiveness</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-green-600">
                              {controlEffectivenessAnalysis.effectiveness_percentage}%
                            </p>
                            <p className="text-xs text-gray-500">Risk Reduction</p>
                          </div>
                        </div>

                        {/* Suggested Residual Values */}
                        {controlEffectivenessAnalysis.suggested_residual_score !== null && (
                          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex items-start gap-3">
                              <Sparkles className="h-5 w-5 text-purple-500 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-purple-900 mb-2">
                                  Suggested Residual Risk (Based on Controls)
                                </p>
                                <div className="flex items-center gap-6 mb-3">
                                  <div className="text-center">
                                    <p className="text-xs text-purple-600 mb-1">Likelihood</p>
                                    <span className="inline-block px-2 py-1 bg-white rounded text-sm font-semibold text-purple-700 border border-purple-200">
                                      {controlEffectivenessAnalysis.suggested_residual_likelihood}
                                    </span>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-xs text-purple-600 mb-1">Impact</p>
                                    <span className="inline-block px-2 py-1 bg-white rounded text-sm font-semibold text-purple-700 border border-purple-200">
                                      {controlEffectivenessAnalysis.suggested_residual_impact}
                                    </span>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-xs text-purple-600 mb-1">Score</p>
                                    <span className={`inline-block px-2 py-1 rounded text-sm font-semibold text-white ${
                                      getRiskLevel(controlEffectivenessAnalysis.suggested_residual_score).color
                                    }`}>
                                      {controlEffectivenessAnalysis.suggested_residual_score}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={handleApplySuggestedResidual}
                                  disabled={applyingSuggestedResidual}
                                  className="flex items-center gap-2 text-sm bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md transition-colors disabled:opacity-50"
                                >
                                  {applyingSuggestedResidual ? (
                                    <>
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                      Applying...
                                    </>
                                  ) : (
                                    <>
                                      <Zap className="h-4 w-4" />
                                      Apply Suggested Values
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Control Breakdown */}
                        {controlEffectivenessAnalysis.control_breakdown.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-2">Control Breakdown</p>
                            <div className="max-h-48 overflow-y-auto space-y-2">
                              {controlEffectivenessAnalysis.control_breakdown.map((control) => (
                                <div
                                  key={control.id}
                                  className={`flex items-center justify-between p-2 rounded border ${
                                    control.has_gap ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
                                  }`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {control.control_number}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">{control.name}</p>
                                  </div>
                                  <div className="flex items-center gap-3 ml-2">
                                    <div className="text-right">
                                      <div className="flex items-center gap-1">
                                        {control.has_gap ? (
                                          <XCircle className="h-4 w-4 text-amber-500" />
                                        ) : (
                                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                                        )}
                                        <span className={`text-xs font-medium ${
                                          control.has_gap ? 'text-amber-600' : 'text-green-600'
                                        }`}>
                                          {control.effectiveness_label}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-400">
                                        {control.risk_reduction_percentage.toFixed(0)}% reduction
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic py-2">
                        No controls linked to this risk. Link controls from the Controls page to see effectiveness analysis.
                      </p>
                    )}
                  </div>

                  {/* Evidence Linking Section */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-500" />
                        Linked Evidence ({editingRisk ? linkedEvidence.length : 0})
                      </h3>
                      {editingRisk && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowEvidencePicker(true)
                            fetchAvailableEvidence()
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Link2 className="h-4 w-4" />
                          Link Evidence
                        </button>
                      )}
                    </div>

                    {!editingRisk ? (
                      <div className="py-2">
                        {/* Empty state placeholder matching edit mode structure */}
                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                          <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 italic">
                            Save the risk first to link supporting evidence.
                          </p>
                        </div>
                      </div>
                    ) : loadingEvidence ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                      </div>
                    ) : linkedEvidence.length === 0 ? (
                      <p className="text-sm text-gray-500 italic py-2">
                        No evidence linked to this risk. Click "Link Evidence" to attach supporting documentation.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {linkedEvidence.map((ev) => (
                          <div
                            key={ev.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {ev.title || ev.description || 'Untitled Evidence'}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className={`px-1.5 py-0.5 rounded ${
                                  ev.relationship_type === 'mitigation'
                                    ? 'bg-green-100 text-green-700'
                                    : ev.relationship_type === 'assessment'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {ev.relationship_type}
                                </span>
                                <span className="text-gray-400">{ev.evidence_type}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleUnlinkEvidence(ev.id)}
                              className="p-1 text-gray-400 hover:text-red-500"
                              title="Unlink evidence"
                            >
                              <Unlink className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* KRI Linking Section (Issue #127) */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <Gauge className="h-4 w-4 text-purple-500" />
                        Linked KRIs ({editingRisk ? linkedKRIs.length : 0})
                      </h3>
                      {editingRisk && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowKRIPicker(true)
                            fetchAvailableKRIs()
                          }}
                          className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                        >
                          <Link2 className="h-4 w-4" />
                          Link KRI
                        </button>
                      )}
                    </div>

                    {!editingRisk ? (
                      <div className="py-2">
                        {/* Empty state placeholder matching edit mode structure */}
                        <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                          <Gauge className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-500 italic">
                            Save the risk first to link Key Risk Indicators.
                          </p>
                        </div>
                      </div>
                    ) : loadingKRIs ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                      </div>
                    ) : linkedKRIs.length === 0 ? (
                      <p className="text-sm text-gray-500 italic py-2">
                        No KRIs linked to this risk. Click "Link KRI" to attach Key Risk Indicators for monitoring.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {linkedKRIs.map((kri) => (
                          <div
                            key={kri.id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {kri.name}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className={`px-1.5 py-0.5 rounded ${
                                  kri.current_status === 'green'
                                    ? 'bg-green-100 text-green-700'
                                    : kri.current_status === 'yellow'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : kri.current_status === 'red'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {kri.current_status || 'No data'}
                                </span>
                                <span className="text-gray-400">
                                  {kri.current_value !== undefined ? `${kri.current_value}` : 'No value'}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleUnlinkKRI(kri.id)}
                              className="p-1 text-gray-400 hover:text-red-500"
                              title="Unlink KRI"
                            >
                              <Unlink className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

              <div>
                <label className="label">Owner</label>
                <select
                  value={formData.owner_id}
                  onChange={(e) =>
                    setFormData({ ...formData, owner_id: e.target.value })
                  }
                  className="input"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username}
                    </option>
                  ))}
                </select>
              </div>

              {/* Organizational Hierarchy (Issue #153) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Business Unit</label>
                  <input
                    type="text"
                    value={formData.business_unit}
                    onChange={(e) =>
                      setFormData({ ...formData, business_unit: e.target.value })
                    }
                    className="input"
                    placeholder="e.g., Sales, Engineering"
                  />
                </div>
                <div>
                  <label className="label">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                    className="input"
                    placeholder="e.g., DevOps, Security"
                  />
                </div>
              </div>

              <div>
                <label className="label">Mitigation Plan</label>
                <textarea
                  value={formData.mitigation_plan}
                  onChange={(e) =>
                    setFormData({ ...formData, mitigation_plan: e.target.value })
                  }
                  className="input"
                  rows={3}
                  placeholder="Describe the plan to mitigate this risk..."
                />
              </div>

              {/* Custom Fields */}
              <CustomFieldsForm
                fields={customFieldDefinitions}
                values={formData.custom_fields}
                onChange={(fieldName, value) =>
                  setFormData({
                    ...formData,
                    custom_fields: { ...formData.custom_fields, [fieldName]: value },
                  })
                }
                loading={loadingCustomFields}
              />

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingRisk ? 'Update Risk' : 'Create Risk'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Evidence Picker Modal */}
      {showEvidencePicker && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => e.target === e.currentTarget && setShowEvidencePicker(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Link Evidence to Risk</h2>
              <button
                onClick={() => setShowEvidencePicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 border-b">
              <label className="label">Relationship Type</label>
              <select
                value={evidenceRelationshipType}
                onChange={(e) => setEvidenceRelationshipType(e.target.value as 'supporting' | 'mitigation' | 'assessment')}
                className="input"
              >
                <option value="supporting">Supporting - Documents the risk</option>
                <option value="mitigation">Mitigation - Shows control effectiveness</option>
                <option value="assessment">Assessment - Risk assessment documentation</option>
              </select>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {availableEvidence.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No evidence available to link</p>
              ) : (
                <div className="space-y-2">
                  {availableEvidence
                    .filter((ev) => !linkedEvidence.some((le) => le.id === ev.id))
                    .map((ev) => (
                      <label
                        key={ev.id}
                        className={`flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-gray-50 ${
                          selectedEvidenceIds.includes(ev.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEvidenceIds.includes(ev.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEvidenceIds([...selectedEvidenceIds, ev.id])
                            } else {
                              setSelectedEvidenceIds(selectedEvidenceIds.filter((id) => id !== ev.id))
                            }
                          }}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {ev.file_name || ev.note || 'Untitled Evidence'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {ev.control_number ? `Control: ${ev.control_number}` : 'No control linked'}
                            {ev.uploaded_by_username && ` - by ${ev.uploaded_by_username}`}
                          </p>
                        </div>
                      </label>
                    ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowEvidencePicker(false)
                  setSelectedEvidenceIds([])
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkEvidence}
                disabled={selectedEvidenceIds.length === 0 || linkingEvidence}
                className="btn-primary disabled:opacity-50"
              >
                {linkingEvidence ? 'Linking...' : `Link ${selectedEvidenceIds.length} Evidence`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KRI Picker Modal (Issue #127) */}
      {showKRIPicker && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => e.target === e.currentTarget && setShowKRIPicker(false)}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Gauge className="h-5 w-5 text-purple-500" />
                Link KRIs to Risk
              </h2>
              <button
                onClick={() => setShowKRIPicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {availableKRIs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No KRIs available to link. Create KRIs from the KRIs page first.
                </p>
              ) : (
                <div className="space-y-2">
                  {availableKRIs.map((kri) => (
                    <label
                      key={kri.id}
                      className={`flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-gray-50 ${
                        selectedKRIIds.includes(kri.id) ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedKRIIds.includes(kri.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedKRIIds([...selectedKRIIds, kri.id])
                          } else {
                            setSelectedKRIIds(selectedKRIIds.filter((id) => id !== kri.id))
                          }
                        }}
                        className="h-4 w-4 text-purple-600 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {kri.name}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="capitalize">{kri.metric_type}</span>
                          <span>|</span>
                          <span className={`px-1.5 py-0.5 rounded ${
                            kri.current_status === 'green'
                              ? 'bg-green-100 text-green-700'
                              : kri.current_status === 'yellow'
                              ? 'bg-yellow-100 text-yellow-700'
                              : kri.current_status === 'red'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {kri.current_status || 'No data'}
                          </span>
                          {kri.current_value !== undefined && (
                            <span>Value: {kri.current_value}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowKRIPicker(false)
                  setSelectedKRIIds([])
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkKRIs}
                disabled={selectedKRIIds.length === 0 || linkingKRIs}
                className="btn-primary disabled:opacity-50"
              >
                {linkingKRIs ? 'Linking...' : `Link ${selectedKRIIds.length} KRI${selectedKRIIds.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Risk Scoring Methodology Modal */}
      <RiskScoringMethodologyModal
        isOpen={showMethodologyModal}
        onClose={() => setShowMethodologyModal(false)}
      />

      {/* Category Create/Edit Modal (Issue #100) */}
      {showCategoryModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && setShowCategoryModal(false)}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Folder className="h-5 w-5 text-primary-600" />
                  {editingCategory ? 'Edit Category' : 'New Category'}
                </h3>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Category Name */}
                <div>
                  <label className="label">Name *</label>
                  <input
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="input"
                    placeholder="e.g., Operational Risk"
                    autoFocus
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="label">Description</label>
                  <textarea
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="input"
                    rows={2}
                    placeholder="Brief description of this risk category"
                  />
                </div>

                {/* Parent Category */}
                <div>
                  <label className="label">Parent Category</label>
                  <select
                    value={categoryFormData.parent_id}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, parent_id: e.target.value }))}
                    className="input"
                  >
                    <option value="">None (Top-Level)</option>
                    {flatCategories
                      .filter(c => c.id !== editingCategory?.id) // Can't be parent of itself
                      .map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Color and Icon */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={categoryFormData.color}
                        onChange={(e) => setCategoryFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={categoryFormData.color}
                        onChange={(e) => setCategoryFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="input flex-1"
                        placeholder="#6B7280"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label">Sort Order</label>
                    <input
                      type="number"
                      value={categoryFormData.sort_order}
                      onChange={(e) => setCategoryFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      className="input"
                      min="0"
                    />
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Preview:</p>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-lg"
                      style={{ backgroundColor: categoryFormData.color }}
                    >
                      <Folder className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium text-gray-900">
                      {categoryFormData.name || 'Category Name'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategory}
                  disabled={savingCategory || !categoryFormData.name.trim()}
                  className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {savingCategory ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {editingCategory ? 'Save Changes' : 'Create Category'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Review Modal (Issue #70) */}
      {showReviewModal && reviewingRisk && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ClipboardCheck className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Record Risk Review</h2>
                    <p className="text-sm text-gray-500">{reviewingRisk.title}</p>
                  </div>
                </div>
                <button onClick={() => setShowReviewModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Current Values (read-only reference) */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Current Values</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Inherent Risk:</span>
                    <span className="ml-2 font-medium">{reviewingRisk.likelihood} × {reviewingRisk.impact} = {reviewingRisk.risk_score}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Residual Risk:</span>
                    <span className="ml-2 font-medium">
                      {reviewingRisk.residual_likelihood || '-'} × {reviewingRisk.residual_impact || '-'} = {reviewingRisk.residual_risk_score || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <span className="ml-2 font-medium capitalize">{reviewingRisk.status}</span>
                  </div>
                </div>
              </div>

              {/* Review Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Likelihood (1-5)</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={reviewFormData.likelihood}
                      onChange={(e) => setReviewFormData({ ...reviewFormData, likelihood: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Impact (1-5)</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      value={reviewFormData.impact}
                      onChange={(e) => setReviewFormData({ ...reviewFormData, impact: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Residual Likelihood (1-5)</label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={reviewFormData.residual_likelihood}
                      onChange={(e) => setReviewFormData({ ...reviewFormData, residual_likelihood: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0 = not set"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Residual Impact (1-5)</label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={reviewFormData.residual_impact}
                      onChange={(e) => setReviewFormData({ ...reviewFormData, residual_impact: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0 = not set"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={reviewFormData.status}
                    onChange={(e) => setReviewFormData({ ...reviewFormData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="open">Open</option>
                    <option value="mitigating">Mitigating</option>
                    <option value="mitigated">Mitigated</option>
                    <option value="accepted">Accepted</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Review Notes</label>
                  <textarea
                    value={reviewFormData.notes}
                    onChange={(e) => setReviewFormData({ ...reviewFormData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Document your review findings and any changes made..."
                  />
                </div>

                {/* Score Preview */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Score Preview</h4>
                  <div className="flex gap-8 text-sm">
                    <div>
                      <span className="text-blue-600">New Inherent:</span>
                      <span className="ml-2 font-bold text-blue-800">
                        {reviewFormData.likelihood * reviewFormData.impact}
                      </span>
                    </div>
                    {reviewFormData.residual_likelihood > 0 && reviewFormData.residual_impact > 0 && (
                      <div>
                        <span className="text-blue-600">New Residual:</span>
                        <span className="ml-2 font-bold text-blue-800">
                          {reviewFormData.residual_likelihood * reviewFormData.residual_impact}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordReview}
                  disabled={savingReview}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {savingReview ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <ClipboardCheck className="h-4 w-4" />
                      Record Review
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Review History Modal (Issue #70) */}
      {showReviewHistoryModal && reviewingRisk && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <History className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Review History</h2>
                    <p className="text-sm text-gray-500">{reviewingRisk.title}</p>
                  </div>
                </div>
                <button onClick={() => setShowReviewHistoryModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {loadingReviewHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full" />
                </div>
              ) : reviewHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No review history available</p>
                  <p className="text-sm mt-1">Reviews will appear here after the first review is recorded.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviewHistory.map((review) => (
                    <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{review.reviewed_by_name || review.reviewed_by_username}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              review.outcome === 'no_change' ? 'bg-gray-100 text-gray-700' :
                              review.outcome === 'score_increased' ? 'bg-red-100 text-red-700' :
                              review.outcome === 'score_decreased' ? 'bg-green-100 text-green-700' :
                              review.outcome === 'status_changed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {review.outcome.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {new Date(review.reviewed_at).toLocaleDateString()} at {new Date(review.reviewed_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>

                      {/* Score Changes */}
                      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                        <div className="p-2 bg-gray-50 rounded">
                          <div className="text-gray-500 text-xs mb-1">Inherent Risk</div>
                          <div className="flex items-center gap-2">
                            <span>{review.likelihood_before} × {review.impact_before}</span>
                            {review.likelihood_after !== review.likelihood_before || review.impact_after !== review.impact_before ? (
                              <>
                                <span className="text-gray-400">→</span>
                                <span className="font-medium">{review.likelihood_after} × {review.impact_after}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                        <div className="p-2 bg-gray-50 rounded">
                          <div className="text-gray-500 text-xs mb-1">Residual Risk</div>
                          <div className="flex items-center gap-2">
                            <span>{review.residual_likelihood_before || '-'} × {review.residual_impact_before || '-'}</span>
                            {(review.residual_likelihood_after !== review.residual_likelihood_before || review.residual_impact_after !== review.residual_impact_before) ? (
                              <>
                                <span className="text-gray-400">→</span>
                                <span className="font-medium">{review.residual_likelihood_after || '-'} × {review.residual_impact_after || '-'}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Status Change */}
                      {review.status_after !== review.status_before && (
                        <div className="text-sm mb-3">
                          <span className="text-gray-500">Status:</span>
                          <span className="ml-2 capitalize">{review.status_before}</span>
                          <span className="mx-2 text-gray-400">→</span>
                          <span className="font-medium capitalize">{review.status_after}</span>
                        </div>
                      )}

                      {/* Notes */}
                      {review.notes && (
                        <div className="text-sm text-gray-600 border-t pt-2 mt-2">
                          {review.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowReviewHistoryModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
